/**
 * Pure per-status card-visuals for StackStep's connection card: given the
 * resolved `cardStatus` and the callbacks/provider it depends on, returns the
 * subtitle and trailing JSX to render. Extracted so StackStep can memoize the
 * result and pass a reference-stable value across the
 * `StackStepConnectionCard` prop boundary.
 */
import type { ReactNode } from 'react';

import { BadgeWithDot } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

const PROVIDER_COPY = {
  google: {
    notConnectedSubtitle:
      'Connect Gmail so Ahoy can work from your inbox and calendar.',
  },
  microsoft: {
    notConnectedSubtitle:
      'Connect Outlook so Ahoy can work from your inbox and calendar.',
  },
} as const;

const CONNECTED_SUBTITLE = 'Ahoy is working from your inbox and calendar.';

const UNKNOWN_PROVIDER_SUBTITLE = 'Ahoy works with Gmail and Outlook.';
const PROFILE_ERROR_MESSAGE = "We couldn't load your account. Try again.";
const STATUS_ERROR_MESSAGE = "We couldn't check your connection. Try again.";

export type ConnectionCardStatus =
  | 'no-org'
  | 'profile-error'
  | 'loading'
  | 'unknown-provider'
  | 'status-error'
  | 'connected'
  | 'not-connected';

export interface StackStepCardVisuals {
  cardSubtitle: { text: string; tone: 'muted' | 'error' } | null;
  statusTrailing: ReactNode;
}

export function computeCardVisuals(args: {
  cardStatus: ConnectionCardStatus;
  provider: 'google' | 'microsoft';
  isConnecting: boolean;
  onConnect: () => void;
  onRetryProfile: () => void;
  onRetryStatus: () => void;
}): StackStepCardVisuals {
  const {
    cardStatus,
    provider,
    isConnecting,
    onConnect,
    onRetryProfile,
    onRetryStatus,
  } = args;

  let cardSubtitle: { text: string; tone: 'muted' | 'error' } | null = null;
  let statusTrailing: ReactNode = null;
  switch (cardStatus) {
    case 'loading':
      statusTrailing = <LoadingIndicator size='xs' />;
      break;
    case 'connected':
      cardSubtitle = { text: CONNECTED_SUBTITLE, tone: 'muted' };
      statusTrailing = (
        <BadgeWithDot color='success' size='md'>
          Connected
        </BadgeWithDot>
      );
      break;
    case 'not-connected':
      cardSubtitle = {
        text: PROVIDER_COPY[provider].notConnectedSubtitle,
        tone: 'muted',
      };
      statusTrailing = (
        <Button
          size='sm'
          onClick={onConnect}
          isDisabled={isConnecting}
          disabledTooltip='Connecting…'
        >
          {isConnecting ? 'Connecting…' : 'Connect'}
        </Button>
      );
      break;
    case 'profile-error':
      cardSubtitle = { text: PROFILE_ERROR_MESSAGE, tone: 'error' };
      statusTrailing = (
        <Button size='sm' color='secondary' onClick={onRetryProfile}>
          Retry
        </Button>
      );
      break;
    case 'status-error':
      cardSubtitle = { text: STATUS_ERROR_MESSAGE, tone: 'error' };
      statusTrailing = (
        <Button size='sm' color='secondary' onClick={onRetryStatus}>
          Retry
        </Button>
      );
      break;
    case 'unknown-provider':
      // Profile resolved but no Google/Microsoft account — suppress every
      // provider-specific status and Connect affordance rather than guessing.
      cardSubtitle = { text: UNKNOWN_PROVIDER_SUBTITLE, tone: 'muted' };
      break;
    case 'no-org':
      break;
  }

  return { cardSubtitle, statusTrailing };
}
